import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithoutRef, HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export function Tarjeta({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-xl border border-border bg-surface shadow-hoja', className)} {...props} />;
}

export function TarjetaEncabezado({ titulo, descripcion, acciones }: { titulo: ReactNode; descripcion?: ReactNode; acciones?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
      <div>
        <h3 className="font-display text-[17px] leading-tight">{titulo}</h3>
        {descripcion && <p className="mt-0.5 text-[12.5px] text-foreground-muted">{descripcion}</p>}
      </div>
      {acciones && <div className="flex shrink-0 items-center gap-2">{acciones}</div>}
    </div>
  );
}

const variantesInsignia = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-medium leading-5 [&_svg]:size-3.5',
  {
    variants: {
      tono: {
        neutro: 'bg-muted text-foreground',
        acento: 'bg-accent-soft text-accent',
        exito: 'bg-success-soft text-success',
        aviso: 'bg-warning-soft text-warning',
        peligro: 'bg-destructive-soft text-destructive',
        info: 'bg-info-soft text-info',
        solido: 'bg-primary text-on-primary',
      },
    },
    defaultVariants: { tono: 'neutro' },
  },
);

export function Insignia({ className, tono, ...props }: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof variantesInsignia>) {
  return <span className={cn(variantesInsignia({ tono }), className)} {...props} />;
}

export function Esqueleto({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} aria-hidden {...props} />;
}

export function Tabla({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full border-collapse text-[13.5px]', className)} {...props} />
    </div>
  );
}

export function Th({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        'border-b border-border bg-surface-alt px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-wide text-foreground-muted',
        className,
      )}
      {...props}
    />
  );
}

export function Td({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('border-b border-border/70 px-3 py-2 align-middle', className)} {...props} />;
}

export function Tr({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('transition-colors hover:bg-surface-alt', className)} {...props} />;
}

export function Vacio({ icono, titulo, descripcion, accion }: { icono?: ReactNode; titulo: string; descripcion?: string; accion?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      {icono && <div className="mb-1 rounded-full bg-muted p-3 text-foreground-muted [&_svg]:size-5">{icono}</div>}
      <p className="font-display text-lg">{titulo}</p>
      {descripcion && <p className="max-w-sm text-[13px] text-foreground-muted">{descripcion}</p>}
      {accion && <div className="mt-2">{accion}</div>}
    </div>
  );
}

export const Pestanas = TabsPrimitive.Root;

export function PestanasLista({ className, ...props }: ComponentPropsWithoutRef<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List className={cn('flex gap-1 border-b border-border', className)} {...props} />;
}

export function Pestana({ className, ...props }: ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        '-mb-px whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-[13.5px] text-foreground-muted transition-colors hover:text-foreground data-[state=active]:border-accent data-[state=active]:font-medium data-[state=active]:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export const PestanaContenido = TabsPrimitive.Content;

/** Número grande para indicadores (KPI). */
export function Indicador({ etiqueta, valor, detalle, icono }: { etiqueta: string; valor: ReactNode; detalle?: ReactNode; icono?: ReactNode }) {
  return (
    <Tarjeta className="p-4">
      <div className="flex items-center justify-between text-[12px] font-medium uppercase tracking-wide text-foreground-muted">
        {etiqueta}
        {icono && <span className="text-accent [&_svg]:size-4">{icono}</span>}
      </div>
      <div className="cifras mt-2 font-display text-[28px] leading-none">{valor}</div>
      {detalle && <div className="mt-1.5 text-[12.5px] text-foreground-muted">{detalle}</div>}
    </Tarjeta>
  );
}
